import React, { useState } from 'react'
import { LogOut, User as UserIcon, Globe, Monitor, Smartphone, RefreshCw, Upload, Clock, AlertTriangle, Calendar, FileJson } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { useEditorStore } from '../stores/editorStore'
import { useAuthStore } from '../stores/authStore'
import { renderApi } from '../services/api'
import toast from 'react-hot-toast'
import { VersioningModal } from './editor/VersioningModal'
import { SchedulingModal } from './editor/SchedulingModal'
import { JsonEditorModal } from './editor/JsonEditorModal'
import StoreSwitcher from './StoreSwitcher'
import { User } from '../types'
import { hasStoreChoice } from '../utils/storeAccess'

interface HeaderProps {
  user: User | null
  onLogout: () => void
}

export default function Header({ user, onLogout }: HeaderProps) {
  const location = useLocation()
  const isEditor = location.pathname.startsWith('/editor/')
  const { previewMode, togglePreviewMode, isLoading, previewWidth } = useEditorStore()
  const { useDevBranch } = useAuthStore()
  const [isPublishClicked, setIsPublishClicked] = useState(false)
  const [showVersionsModal, setShowVersionsModal] = useState(false)
  const [showResetModal, setShowResetModal] = useState(false)
  const [showSchedulingModal, setShowSchedulingModal] = useState(false)
  const [showJsonEditorModal, setShowJsonEditorModal] = useState(false)
  const [isResetting, setIsResetting] = useState(false)

  const handleReset = async () => {
    try {
      setIsResetting(true)
      await renderApi.reset()
      toast.success('JSON resetado com sucesso. Recarregando editor...')
      setShowResetModal(false)

      // Força o recarregamento da página para sincronizar o estado do frontend.
      setTimeout(() => {
        window.location.reload();
      }, 1000); // Delay de 1s para o usuário ler o toast.

    } catch (e) {
      toast.error('Erro ao resetar')
    } finally {
      setIsResetting(false)
    }
  }

  // Persiste alterações pendentes do painel de propriedades antes de publicar.
  // saveChanges() engole os erros, então usamos applyAndRefreshPreview para que
  // uma falha de persistência aborte a publicação em vez de publicar o estado antigo.
  const flushPendingChanges = async () => {
    const { isDirty, components, applyAndRefreshPreview } = useEditorStore.getState()
    if (isDirty && components.length > 0) {
      await applyAndRefreshPreview(components)
    }
  }

  const handlePublish = async () => {
    setIsPublishClicked(true)

    try {
      await flushPendingChanges()
      // Publicar conteúdo e estilos na Wake
      await renderApi.publish()
      toast.success('Configuração salva e publicada')
    } catch (e) {
      toast.error('Erro ao publicar configuração')
    } finally {
      // Reset do estado após um breve delay para mostrar o feedback
      setTimeout(() => setIsPublishClicked(false), 600)
    }
  }

  const handlePublishDev = async () => {
    setIsPublishClicked(true)
    
    try {
      await flushPendingChanges()
      // Publicar layout do settings.json e estilos na Wake (dev mode)
      await renderApi.publishDev()
      toast.success('Layout e estilos publicados na Wake API')
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Erro ao publicar na Wake API')
    } finally {
      // Reset do estado após um breve delay para mostrar o feedback
      setTimeout(() => setIsPublishClicked(false), 600)
    }
  }

  return (
    <header className="bg-white border-b border-gray-200 px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {/* Logo */}
          <div className="flex flex-col items-center space-x-1">
            <img 
              src="/logo.png" 
              alt="Site Editor Wake" 
              className="h-6 w-auto"
              onError={(e) => {
                // Fallback se o logo não existir
                e.currentTarget.style.display = 'none';
              }}
            />
            <h1 className="text-xs text-gray-900 leading-none pt-[1px]">
              Site Editor
            </h1>
          </div>
          {user && (
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              {hasStoreChoice(user.allowedStores) || user.isMultiStore ? (
                <StoreSwitcher />
              ) : (
                <span>{user.storeName}</span>
              )}
              {useDevBranch && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  DEV MODE
                </span>
              )}
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-4">
          {isEditor && (
            <div className="flex items-center space-x-2">
              {/* Toggle Desktop */}
              <button
                onClick={() => previewMode !== 'desktop' && togglePreviewMode()}
                className={`flex items-center space-x-2 px-3 py-1.5 rounded-md border transition-all duration-200 ${
                  previewMode === 'desktop'
                    ? 'bg-primary-50 border-primary-500 text-primary-500'
                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                }`}
                title="Visualização Desktop"
              >
                <Monitor className="w-4 h-4" />
                {previewMode === 'desktop' && (
                  <span className="text-sm font-medium">Desktop</span>
                )}
              </button>
              {/* Toggle Mobile */}
              <button
                onClick={() => previewMode !== 'mobile' && togglePreviewMode()}
                className={`flex items-center space-x-2 px-3 py-1.5 rounded-md border transition-all duration-200 ${
                  previewMode === 'mobile'
                    ? 'bg-primary-50 border-primary-500 text-primary-500'
                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                }`}
                title="Visualização Mobile"
              >
                <Smartphone className="w-4 h-4" />
                {previewMode === 'mobile' && (
                  <span className="text-sm font-medium">Mobile</span>
                )}
              </button>

              {/* Largura do preview */}
              <div className="ml-2 text-xs text-gray-500 tabular-nums">
                {previewWidth ? `${previewWidth}px` : '--'}
              </div>

              {/* Reset - agora visível em ambos os modos (normal e dev) */}
                <button
                  onClick={() => setShowResetModal(true)}
                  disabled={isLoading || isResetting}
                  className="btn btn-secondary flex items-center space-x-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  title="Resetar JSON de edição para o estado da ultima publicação"
                >
                  <RefreshCw className={`w-4 h-4 ${isResetting ? 'animate-spin' : ''}`} />
                  <span>{isResetting ? 'Sync/Reset...' : 'Sync/Reset'}</span>
                </button>

              {/* Versões */}
              <button
                onClick={() => setShowVersionsModal(true)}
                className="btn btn-secondary flex items-center space-x-2"
                title="Ver últimas versões"
              >
                <Clock className="w-4 h-4" />
                <span>Versões</span>
              </button>

              {/* Agendar - agora visível em ambos os modos */}
                <button
                  onClick={() => setShowSchedulingModal(true)}
                  className="btn btn-outline flex items-center space-x-2"
                  title="Agendar publicação"
                >
                  <Calendar className="w-4 h-4" />
                  <span>Agendar</span>
                </button>

              {/* Publish - Ocultar em dev mode */}
              {!useDevBranch && (
                <button
                  onClick={handlePublish}
                  className={`
                    btn flex items-center space-x-2 transition-all duration-200 text-white
                    ${isPublishClicked 
                      ? 'bg-green-600 scale-[0.98] hover:bg-green-600' 
                      : 'bg-gradient-primary hover:opacity-90'
                    }
                  `}
                >
                  <Upload className="w-4 h-4" />
                  <span>{isPublishClicked ? 'Publicando' : 'Publicar'}</span>
                </button>
              )}

              {/* JSON Editor - Apenas em dev mode */}
              {useDevBranch && (
                <button
                  onClick={() => setShowJsonEditorModal(true)}
                  className="btn btn-secondary flex items-center space-x-2"
                  title="Editar layout JSON do settings.json"
                >
                  <FileJson className="w-4 h-4" />
                  <span>JSON</span>
                </button>
              )}

              {/* Publish Dev - Apenas em dev mode */}
              {useDevBranch && (
                <button
                  onClick={handlePublishDev}
                  className={`
                    btn flex items-center space-x-2 transition-all duration-200 text-white
                    ${isPublishClicked 
                      ? 'bg-green-600 scale-[0.98] hover:bg-green-600' 
                      : 'bg-gradient-primary hover:opacity-90'
                    }
                  `}
                  title="Publicar layout do settings.json e estilos na Wake API"
                >
                  <Upload className="w-4 h-4" />
                  <span>{isPublishClicked ? 'Publicando...' : 'Publicar'}</span>
                </button>
              )}
            </div>
          )}
          
          <button
            onClick={onLogout}
            className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span>Sair</span>
          </button>
        </div>
      </div>

      {/* Modal de Versionamento */}
      <VersioningModal
        isOpen={showVersionsModal}
        onClose={() => setShowVersionsModal(false)}
      />

      {/* Modal de Agendamento */}
      <SchedulingModal
        isOpen={showSchedulingModal}
        onClose={() => setShowSchedulingModal(false)}
      />

      {/* Modal de Editor JSON (dev mode) */}
      <JsonEditorModal
        isOpen={showJsonEditorModal}
        onClose={() => setShowJsonEditorModal(false)}
      />

      {/* Modal de Confirmação de Reset */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-md max-w-md w-full mx-4 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-primary-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Confirmar Reset
                </h3>
                <p className="text-sm text-gray-600">
                  Esta ação irá reverter todas as mudanças
                </p>
              </div>
            </div>
            
            <div className="mb-4">
              <p className="text-gray-700 mb-2">
                Você está prestes a resetar o editor para o estado da <strong>última publicação na Wake</strong>.
              </p>
              <div className="bg-primary-50 rounded-md p-3">
                <p className="text-sm text-primary-700">
                  ⚠️ <strong>Atenção:</strong> Todas as mudanças não publicadas serão perdidas.
                </p>
              </div>
              <p className="text-gray-600 text-sm mt-2">
                Tem certeza que deseja continuar?
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => !isResetting && setShowResetModal(false)}
                disabled={isResetting}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              <button
                onClick={handleReset}
                disabled={isLoading || isResetting}
                className="flex-1 px-4 py-2 bg-gradient-primary text-white rounded-md hover:opacity-90 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
              >
                {isResetting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Resetando...</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    <span>Confirmar Reset</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
